import { useState, type FormEvent } from 'react';
import { api, type Plan } from '../lib/api.ts';
import { ZONES } from '../lib/plantime.ts';
import { ErrorNote, Field, useAction } from '../ui.tsx';

type Role = 'mother' | 'father' | 'other';

/** First run: the child, the two parents, and whose time the record measures. */
export function PlanSetup({ onCreated }: { onCreated: (plan: Plan) => void }) {
  const [child, setChild] = useState({ name: '', birthDate: '' });
  const [parties, setParties] = useState<{ label: string; role: Role }[]>([
    { label: 'Mother', role: 'mother' },
    { label: 'Father', role: 'father' },
  ]);
  const [defaultParty, setDefaultParty] = useState(1);
  const [timeZone, setTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [shares, setShares] = useState(['', '']);
  const [exchange, setExchange] = useState('');
  const { busy, error, run } = useAction();
  const measured = defaultParty === 0 ? 1 : 0;

  const setParty = (i: number, patch: Partial<{ label: string; role: Role }>) =>
    setParties(parties.map((p, j) => (j === i ? { ...p, ...patch } : p)));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const stated = shares.every((s) => s.trim() !== '') ? shares.map(Number) : undefined;
    void run(async () => {
      const plan = await api.createPlan({
        name: 'Parenting plan',
        timeZone,
        child: { name: child.name.trim(), ...(child.birthDate ? { birthDate: child.birthDate } : {}) },
        parties,
        defaultParty,
        measuredParty: measured,
        ...(stated ? { statedShares: stated } : {}),
        ...(exchange.trim() ? { exchangeLocation: exchange.trim() } : {}),
      });
      onCreated(plan);
    });
  };

  return (
    <main className="canvas" style={{ maxWidth: 760 }}>
      <div className="title-row">
        <div>
          <h1>Set up your plan</h1>
          <p className="finding">
            Who the plan covers, and whose time to measure. After this you’ll enter the schedule, clause by clause.
          </p>
        </div>
      </div>
      <form className="stack" onSubmit={submit}>
        <section className="panel form">
          <div className="panel-head">
            <h2>Your child</h2>
          </div>
          <div className="two">
            <Field label="First name">
              <input className="input" required value={child.name} onChange={(e) => setChild({ ...child, name: e.target.value })} />
            </Field>
            <Field label="Date of birth" hint="Optional. Used for the child’s birthday if the plan has a rule for it.">
              <input className="input" type="date" value={child.birthDate} onChange={(e) => setChild({ ...child, birthDate: e.target.value })} />
            </Field>
          </div>
        </section>

        <section className="panel form">
          <div className="panel-head">
            <h2>The parents</h2>
            <span className="meta">As the plan names them</span>
          </div>
          {parties.map((p, i) => (
            <div className="two" key={i}>
              <Field label={i === 0 ? 'First parent' : 'Second parent'}>
                <input className="input" required value={p.label} onChange={(e) => setParty(i, { label: e.target.value })} />
              </Field>
              <Field label="Role in the plan">
                <select className="input" value={p.role} onChange={(e) => setParty(i, { role: e.target.value as Role })}>
                  <option value="mother">Mother</option>
                  <option value="father">Father</option>
                  <option value="other">Other</option>
                </select>
              </Field>
            </div>
          ))}
          <fieldset className="field">
            <legend>Who has your child when no rule says otherwise?</legend>
            <div className="seg" role="group">
              {parties.map((p, i) => (
                <button type="button" key={i} aria-pressed={defaultParty === i} onClick={() => setDefaultParty(i)}>
                  {p.label || (i === 0 ? 'First parent' : 'Second parent')}
                </button>
              ))}
            </div>
            <span className="hint">
              Usually the primary physical custodian. Kept measures the other parent’s visits: <b>{parties[measured]?.label}</b>.
            </span>
          </fieldset>
        </section>

        <section className="panel form">
          <div className="panel-head">
            <h2>Plan details</h2>
            <span className="meta">Optional</span>
          </div>
          <Field label="Home time zone" hint="Every time in the plan is read in this zone, wherever you are.">
            <select className="input" value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
              {ZONES.map((z) => (
                <option key={z}>{z}</option>
              ))}
            </select>
          </Field>
          <div className="two">
            {parties.map((p, i) => (
              <Field key={i} label={`${p.label || 'Parent'}’s share, as the plan states it`} hint="Percent, e.g. 20">
                <input
                  className="input"
                  inputMode="decimal"
                  value={shares[i]}
                  onChange={(e) => setShares(shares.map((s, j) => (j === i ? e.target.value : s)))}
                />
              </Field>
            ))}
          </div>
          <Field label="Exchange location">
            <input className="input" value={exchange} onChange={(e) => setExchange(e.target.value)} />
          </Field>
        </section>

        <ErrorNote error={error} />
        <div className="actions">
          <button className="btn btn-primary" disabled={busy}>
            Save and enter the schedule
          </button>
        </div>
      </form>
    </main>
  );
}
